// The notification model: types x channels, the defaults, and the resolver.
//
// Decided 2026-10-14 (docs/plan-notifications.md). Replaces the nine per-group
// on/off columns in `notification_prefs`, which switched a whole TYPE and never
// picked a CHANNEL.
//
// RESOLUTION, per (type, channel), first match wins:
//   1. the member's per-group override, if one exists for this group;
//   2. else the member's own default, if they set one;
//   3. else the system default below.
// The answer is then ANDed with "is this channel OFFERED for this type" and "can
// this channel reach them at all". The first AND is what makes an A2P violation
// unrepresentable rather than merely unlikely.
//
// TWO SIGNUP TEMPLATES, with no template column. An email signup is zero rows:
// the system default has every SMS cell off, which is what opt-in means. A text
// signup, once the member ticks the (unchecked-by-default) consent box, WRITES
// the SMS-on cells as that member's own defaults -- `TEXT_SIGNUP_SMS_ON`. So the
// layers stay sparse and nothing needs to remember which template was chosen.
// The cost: retuning the text template later does not reach people who already
// signed up. Accepted.
export const NOTIF_TYPES = [
    'dayOf',
    'newEvent',
    'headcount',
    'eventOn',
    'eventFull',
    'eventChanged',
    'newPost',
    'eventChat',
    'reaction',
    'rsvpChanges',
    'joinRequest',
    'tournamentTable',
];
export const NOTIF_CHANNELS = ['email', 'sms', 'push'];
export function isNotifType(v) {
    return typeof v === 'string' && NOTIF_TYPES.includes(v);
}
export function isNotifChannel(v) {
    return typeof v === 'string' && NOTIF_CHANNELS.includes(v);
}
/**
 * Whether the amended 10DLC campaign has been approved. Until it is, SMS is
 * offered only for the kinds the registered campaign declares.
 *
 * ⚠ FLIPPED TO TRUE 2026-10-20: campaign CM8242f0ea99b94e158d73ac2e175cf567 was approved and
 * the app sends from +19704067113 on the new Messaging Service (docs/a2p-campaign.md).
 * Do not set it back without checking which campaign the sending number is on.
 * It is the single switch between "build the whole model now" and "text a kind
 * the carrier has not approved".
 */
export const SMS_CAMPAIGN_AMENDED = true;
/** SMS kinds the REGISTERED campaign declares (the others are system messages). */
const SMS_REGISTERED = ['newEvent', 'dayOf', 'headcount'];
/**
 * Never SMS, amended or not. Reactions are noise; tournament seating and event changes were
 * never declared in the A2P registration.
 */
const SMS_NEVER = ['reaction', 'tournamentTable', 'eventChanged'];
/** Email is not offered for these: a reaction or a table number is not worth a mail. */
const EMAIL_NEVER = ['reaction', 'tournamentTable'];
export function isOffered(type, channel, campaignAmended = SMS_CAMPAIGN_AMENDED) {
    switch (channel) {
        case 'push':
            return true;
        case 'email':
            return !EMAIL_NEVER.includes(type);
        case 'sms':
            if (SMS_NEVER.includes(type))
                return false;
            return campaignAmended ? true : SMS_REGISTERED.includes(type);
    }
}
/**
 * The system default, i.e. the EMAIL-signup template. Every SMS cell is off.
 * Cells that are not offered are `false` here and never consulted.
 */
export const SYSTEM_DEFAULTS = {
    dayOf: { email: true, sms: false, push: true },
    newEvent: { email: true, sms: false, push: true },
    headcount: { email: true, sms: false, push: true },
    eventOn: { email: true, sms: false, push: true },
    eventFull: { email: true, sms: false, push: true },
    eventChanged: { email: true, sms: false, push: true },
    newPost: { email: false, sms: false, push: true },
    eventChat: { email: false, sms: false, push: true },
    reaction: { email: false, sms: false, push: true },
    rsvpChanges: { email: false, sms: false, push: false },
    joinRequest: { email: true, sms: false, push: true },
    tournamentTable: { email: false, sms: false, push: true },
};
/**
 * What a TEXT signup turns on once the member consents: the SMS cells that are
 * ON in the text template. RSVP changes and join requests are offered by SMS but
 * deliberately stay off. Filtered through `isOffered()` at write time, so nothing
 * unapproved is ever written while the campaign amendment is pending.
 */
export const TEXT_SIGNUP_SMS_ON = [
    'dayOf',
    'newEvent',
    'headcount',
    'eventOn',
    'eventFull',
    'newPost',
    'eventChat',
];
/** The SMS defaults to write for a consenting text signup, given the campaign state. */
export function textSignupSmsDefaults(campaignAmended = SMS_CAMPAIGN_AMENDED) {
    return TEXT_SIGNUP_SMS_ON.filter((t) => isOffered(t, 'sms', campaignAmended));
}
/** Should this (type, channel) fire for this member in this group? */
export function resolveChannel(type, channel, input) {
    const amended = input.campaignAmended ?? SMS_CAMPAIGN_AMENDED;
    if (!isOffered(type, channel, amended))
        return false;
    if (!input.reachable)
        return false;
    return input.override ?? input.own ?? SYSTEM_DEFAULTS[type][channel];
}
export function resolveSource(input) {
    if (input.override !== undefined)
        return 'override';
    if (input.own !== undefined)
        return 'own';
    return 'system';
}
/** Copy for the settings grid. The day-of row is first on purpose (decision Q4). */
export const NOTIF_TYPE_LABELS = {
    dayOf: {
        label: 'Day-of check-in',
        help: 'The “still in?” message on the day of an event you said yes to. It is the reason MySocialCal exists, so think twice before turning it off.',
    },
    newEvent: { label: 'New events', help: 'When someone schedules an event in this group.' },
    headcount: {
        label: 'Headcount summary',
        help: 'The organizer’s summary of who is coming. Organizers only.',
    },
    eventOn: {
        label: 'Event is on or off',
        help: 'When enough people have said yes for an event to go ahead, and if it later falls short again.',
    },
    eventFull: { label: 'Event is full', help: 'When an event reaches its maximum.' },
    eventChanged: {
        label: 'Event changes',
        help: 'When the date, time or place changes on an event you said yes or maybe to.',
    },
    newPost: {
        label: 'New posts',
        help: 'When someone posts to this group. Not event announcements, those are “New events”.',
    },
    eventChat: {
        label: 'Event chat',
        help: 'Messages in an event’s chat. Saying no to the event stops these.',
    },
    reaction: { label: 'Reactions', help: 'When someone reacts to one of your posts.' },
    rsvpChanges: {
        label: 'RSVP changes',
        help: 'When someone responds to an event you organize. Organizers only.',
    },
    joinRequest: {
        label: 'Join requests',
        help: 'When someone asks to join this group. Owners and admins only.',
    },
    tournamentTable: {
        label: 'Tournament tables',
        help: 'A push telling you your table when a round is drawn. Push only.',
    },
};
/**
 * The legacy per-group switch keys (`shared/src/notify.ts`) as views over the
 * model. The 1.0 iOS app and the email-footer page still speak this shape, so
 * the old endpoints translate through this map rather than reading the retired
 * `notification_prefs` columns. `notifyEventReminder` covers headcount too,
 * because that is the switch the organizer's summary always honoured.
 */
export const LEGACY_KEY_TYPES = {
    notifyEventReminder: ['dayOf', 'headcount'],
    notifyFeedPost: ['newEvent'],
    notifyMinReached: ['eventOn'],
    notifyMaxReached: ['eventFull'],
    notifyMemberPost: ['newPost'],
    notifyReactionActivity: ['reaction'],
    notifyRsvpUpdates: ['rsvpChanges'],
    notifyJoinRequest: ['joinRequest'],
    notifyTournamentRound: ['tournamentTable'],
};
//# sourceMappingURL=notifyModel.js.map